import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo, KenyaStripe } from "@/components/Logo";
import { Search, Filter, Star, Users2, ArrowLeft, Globe } from "lucide-react";

const CATEGORIES = ["all", "A", "B", "C", "D"];

export default function PlayersDirectory() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [teamId, setTeamId] = useState("all");
  const [intlOnly, setIntlOnly] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([
          api.get("/public/players?limit=500"),
          api.get("/public/teams"),
        ]);
        setPlayers(p.data || []);
        setTeams(t.data || []);
      } catch (e) { console.error("[PlayersDirectory] Failed to load directory", e); }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return players.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (teamId !== "all" && p.sold_to_team_id !== teamId) return false;
      if (intlOnly && !p.is_international) return false;
      if (qq) {
        const blob = `${p.name} ${p.country || ""}`.toLowerCase();
        if (!blob.includes(qq)) return false;
      }
      return true;
    });
  }, [players, q, category, teamId, intlOnly]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "Free Agent";

  return (
    <div className="min-h-screen bg-[#060d09] text-white">
      <div className="kenya-stripe" />
      <header className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/"><Logo variant="inline" className="h-10" /></Link>
        <nav className="hidden md:flex items-center gap-5 text-xs uppercase tracking-widest text-zinc-400">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/standings" className="hover:text-white">Standings</Link>
          <Link to="/live" className="hover:text-white">Live</Link>
          <Link to="/register-player" className="hover:text-[#00A86B]">Register</Link>
        </nav>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-8 pb-14" data-testid="public-players-directory">
        <Link to="/" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center mb-3" data-testid="players-back-home"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Home</Link>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400] font-bold">The Talent · Public Directory</div>
        <h1 className="font-anton text-6xl md:text-7xl uppercase tracking-tighter" data-testid="players-page-title">All Players.</h1>
        <KenyaStripe className="mt-3 w-24" />

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="players-filters">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="players-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or country…" className="bg-[#11241b] border-white/10 text-white pl-10 h-11" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-36 bg-[#11241b] border-white/10 text-white h-11" data-testid="players-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-[#11241b] border-white/10 text-white">
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : `Category ${c}`}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="w-48 bg-[#11241b] border-white/10 text-white h-11" data-testid="players-team"><SelectValue placeholder="Team" /></SelectTrigger>
            <SelectContent className="bg-[#11241b] border-white/10 text-white">
              <SelectItem value="all">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 px-3 h-11 rounded-md bg-[#11241b] border border-white/10 text-xs uppercase tracking-widest text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={intlOnly} onChange={(e) => setIntlOnly(e.target.checked)} data-testid="players-intl-only" />
            International only
          </label>
        </div>

        <div className="text-xs text-zinc-500 mt-3" data-testid="players-result-count">{loading ? "Loading…" : `${filtered.length} of ${players.length} players`}</div>

        {/* Grid */}
        {!loading && filtered.length === 0 ? (
          <Card className="p-12 text-center bg-[#11241b] border-white/10 border-dashed mt-8" data-testid="players-empty">
            <Users2 className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
            <div className="font-anton text-2xl uppercase text-white">No Players Match</div>
            <p className="text-sm text-zinc-400 mt-2">Try clearing some filters or check back closer to draft day.</p>
          </Card>
        ) : (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="players-grid">
            {filtered.map((p) => (
              <Card key={p.id} className="relative overflow-hidden bg-[#11241b] border-white/10 group hover:border-[#F4B400]/50 transition-colors" data-testid={`public-player-${p.id}`}>
                <div className="aspect-[3/4] relative">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#16322a] to-[#06210f]">
                      <span className="font-anton text-6xl text-[#F4B400]/40">{p.name?.slice(0, 1) || "?"}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    <span className="text-[9px] uppercase tracking-widest bg-[#F4B400] text-black px-1.5 py-0.5 rounded font-bold">{p.tier || "—"}</span>
                    {p.is_international && <span className="text-[9px] uppercase tracking-widest bg-[#E32424] text-white px-1.5 py-0.5 rounded font-bold flex items-center gap-1"><Star className="w-2.5 h-2.5" /> INTL</span>}
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="font-anton text-sm uppercase tracking-tight text-white truncate">{p.name}</div>
                    <div className="text-[10px] text-zinc-300 flex items-center gap-1">
                      <span className="capitalize">{p.category}</span>
                      <span>·</span>
                      <span className="text-[#F4B400] font-bold">{p.rating}</span>
                      {p.country && <><span>·</span><span className="truncate"><Globe className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />{p.country}</span></>}
                    </div>
                    {p.sold_to_team_id && <div className="text-[9px] uppercase tracking-widest text-[#00A86B] mt-0.5 truncate">{teamName(p.sold_to_team_id)}</div>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <div className="kenya-stripe" />
    </div>
  );
}
